import * as React from 'react';
import styled from 'styled-components';
import { ContentSpacing } from '@estee/elc-base-theme';
import { observer } from 'mobx-react';
import { configConsumer, translate } from '@estee/elc-service';

import { classes } from '../../utils/Classes';
import {
    VTO_PERFECT_SHADES_BUTTON,
    VTO_ALL_SHADES_BUTTON,
    VTO_PAIR_SHADES_BUTTON
} from '../../constants/DataTestIdAttributes';
import { IVtoFeature, IVtoFoundationTabs } from '../../interfaces/IVto';
import { foundationTabs } from '../../constants/Vto';

interface IVtoGridTabs {
    translations: {
        seeAllShadesLabel: string;
        seeBestShadesLabel: string;
        seePairShadesLabel: string;
    };
    onTabClick(event: React.MouseEvent<HTMLDivElement>): void;
    tabs: IVtoFoundationTabs;
    config: {
        vtoFeature: IVtoFeature;
    };
}

const Wrapper = styled.div`
    display: flex;
`;

const VtoTab = styled.div`
    flex: 1;
    text-align: center;
    padding: ${ContentSpacing.space12} 0;

    &.disabled {
        pointer-events: none;
        opacity: 0.3;
    }
`;

const vtoTabContainerClassName = classes('vto-tab-container');

export const VtoGridTabs = configConsumer(['vtoFeature'])(
    translate(['seeAllShadesLabel', 'seeBestShadesLabel', 'seePairShadesLabel'])(
        observer(({ translations, onTabClick, tabs, config }: IVtoGridTabs) => {
            const { seeAllShadesLabel, seeBestShadesLabel, seePairShadesLabel } = translations;
            const { perfectShades, pairShades, allShades } = tabs;
            const vtoTabPerfectShadesClassNames = classes('vto-tab', {
                'tab-active': perfectShades.active,
                disabled: perfectShades.disabled
            });
            const vtoTabPairShadesClassNames = classes('vto-tab', {
                'tab-active': pairShades.active,
                disabled: pairShades.disabled
            });
            const vtoTabAllShadesClassNames = classes('vto-tab', {
                'tab-active': allShades.active,
                disabled: allShades.disabled
            });

            const { perfectShadesTab, allShadesTab, pairShadesTab } = foundationTabs;

            if (
                !(
                    config.vtoFeature &&
                    config.vtoFeature.foundation &&
                    config.vtoFeature.foundation.hasLipVto
                )
            ) {
                return null;
            }

            return (
                <Wrapper className={vtoTabContainerClassName}>
                    <VtoTab
                        onClick={onTabClick}
                        data-tab={perfectShadesTab}
                        data-test-id={VTO_PERFECT_SHADES_BUTTON}
                        className={vtoTabPerfectShadesClassNames}
                    >
                        {seeBestShadesLabel}
                    </VtoTab>
                    <VtoTab
                        onClick={onTabClick}
                        data-tab={allShadesTab}
                        data-test-id={VTO_ALL_SHADES_BUTTON}
                        className={vtoTabAllShadesClassNames}
                    >
                        {seeAllShadesLabel}
                    </VtoTab>
                    <VtoTab
                        onClick={onTabClick}
                        data-tab={pairShadesTab}
                        data-test-id={VTO_PAIR_SHADES_BUTTON}
                        className={vtoTabPairShadesClassNames}
                    >
                        {seePairShadesLabel}
                    </VtoTab>
                </Wrapper>
            );
        })
    )
);
